import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { userConstants } from "actions/types";
import { scrollToTop } from "utils/dom";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { getUserState, updateSelectedAccountType } from "actions/userActions";
import { getIraAccountIdSelector } from "store/selectors/user";
import { INVESTMENT_PROFILE } from "statics/onboardingSteps";
import { Col, Row } from "react-bootstrap";
import {
  accountTypesToEnglishMapping,
  TRADITIONAL_AND_ROTH_IRA,
} from "statics/accountTypes";

import { userIs401kRollIn } from "../../../store/selectors/user";

import {
  hasBothPreTaxAndRothFundsSelector,
  hasPreTaxFundsSelector,
  hasRothFundsSelector,
  paychexPerksSelector,
} from "store/selectors/auth0";

import ProgressBar from "components/ProgressBar";
import Button from "components/Button";
import Alert from "components/Alert";
import CircularProgressBar from "components/CircularProgressBar";
import IconHeader from "components/IconHeader";
import IconInfoCard from "components/IconInfoCard";
import Individual401kPartialRollover from "./Individual401kPartialRollover";
import type { RootState } from "types/state";
import type { AccountType } from "types/graphql";

import "./Individual401kConfirmRolloverAccountType.scss";

const actions = [userConstants.UPDATE_SELECTED_ACCOUNT_TYPE];

const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  // user.userState is shallow-typed as `object` in RootState (Redux layer
  // scheduled for deletion) — narrow at the boundary to read the `.state` field.
  const userState = (state.user.userState as { state: string }).state;
  const accounts = state.accounts.iraAccounts;

  return {
    isSubmitting: loadingSelector(state),
    error: errorSelector(state),
    userState,
    // narrow away `undefined` from the shallow-typed selector
    // (getIraAccountIdSelector returns string | null | undefined)
    accountId: getIraAccountIdSelector(state) as string | null,
    accounts,
    hasMixedFunds: hasBothPreTaxAndRothFundsSelector(state),
    hasPreTaxFunds: hasPreTaxFundsSelector(state),
    hasRothFunds: hasRothFundsSelector(state),
    isPaychex: userIs401kRollIn(state),
    isPaychexPerks: paychexPerksSelector(state),
  };
};

const mapDispatchToProps = {
  getUserState,
  updateSelectedAccountType,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  isRothIRASelected: boolean;
  selectedAccountType: string;
  submitted: boolean;
  showSelectAccountType: boolean;
}

interface CardSection {
  key: number;
  title: string;
  content: React.ReactNode;
}

interface CardData {
  title?: string;
  sections: CardSection[];
}

class Individual401kConfirmRolloverAccountType extends React.Component<
  Props,
  State
> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    const paychex401kAccountType = this.props.hasPreTaxFunds
      ? "TRADITIONAL_IRA"
      : "ROTH_IRA";

    this.state = {
      isRothIRASelected: false,
      selectedAccountType: paychex401kAccountType,
      submitted: false,
      showSelectAccountType: false,
    };
  }

  componentDidMount() {
    window.analytics.page("Individual Account Type Selection");
    scrollToTop();
  }

  _submitAccountType = () => {
    const accountType = this.props.hasMixedFunds
      ? TRADITIONAL_AND_ROTH_IRA
      : this.state.selectedAccountType;

    if (this.props.hasMixedFunds) {
      window.analytics.track("Traditional and Roth IRA Selected");
    } else if (accountType === "TRADITIONAL_IRA") {
      window.analytics.track("Traditional IRA Selected");
    } else if (accountType === "ROTH_IRA") {
      window.analytics.track("Roth IRA Selected");
    }

    void this.props
      .updateSelectedAccountType(
        this.props.client as ApolloClient<object>,
        // accountType derives from state (string); the action accepts the
        // narrow AccountType union — cast at the call boundary.
        accountType as AccountType
      )
      .then(() => {
        void this.props.getUserState(this.props.client as ApolloClient<object>);
      });
    window.analytics.track("Submitted Individual 401k Rollover Account Type", {
      userState: this.props.userState,
    });

    this.setState({
      submitted: true,
    });
  };

  _setSelectedAccountType = (selectedAccountType: string) => {
    this.setState({
      selectedAccountType,
    });
  };

  _render401kRolloverConfirmation = () => {
    var articleStyle = {
      paddingBottom: 0,
    };

    let iraSubtext;
    let paychexSubtext;

    if (this.props.hasMixedFunds) {
      iraSubtext =
        "Your 401(k) will roll over into a Traditional IRA and a Roth IRA, to keep the same tax benefits.";
      paychexSubtext = "Your Paychex 401(k) has pre-tax and post-tax savings.";
    } else if (this.props.hasPreTaxFunds) {
      iraSubtext =
        "Your 401(k) will roll over into a Traditional IRA, to keep the same tax benefits.";
      paychexSubtext = "Your Paychex 401(k) has pre-tax savings.";
    } else if (this.props.hasRothFunds) {
      iraSubtext =
        "Your 401(k) will roll over into a Roth IRA, to keep the same tax benefits.";
      paychexSubtext = "Your Paychex 401(k) has post-tax savings.";
    }

    const singleAccountCardData: CardData = {
      sections: [
        {
          key: 1,
          title: "Same tax benefits.",

          content: (
            <div
              style={{
                display: "flex",
              }}
            >
              <p style={{ fontSize: 14 }}>
                Rolling over from a 401(k) to an IRA doesn’t trigger taxes—as
                long as it’s done properly. We make sure your savings stay tax
                advantaged, just like they are now.
              </p>
            </div>
          ),
        },
      ],
    };

    const multiple401kCardData: CardData = {
      sections: [
        {
          key: 1,
          title: "Why You’ll Open Two Accounts",

          content: (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <p style={{ fontSize: 14 }}>
                If your Paychex 401(k) includes both Traditional and Roth
                contributions, you’ll need to open both a Traditional and a Roth
                IRA.
              </p>
              <p style={{ fontSize: 14 }}>
                This keeps your tax-advantaged savings in the right type of
                account—just like they were in your 401(k). Don’t worry, we’ll
                guide you through it.
              </p>
            </div>
          ),
        },
        {
          key: 2,
          title: "Access your money ",

          content: (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                marginBottom: "8px",
              }}
            >
              <p style={{ fontSize: 14 }}>
                Withdraw penalty-free for certain life events, like a first-time
                home purchase, a birth, or college expenses.
              </p>
            </div>
          ),
        },
        {
          key: 3,
          title: "All-in-One",

          content: (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                marginBottom: "8px",
              }}
            >
              <p style={{ fontSize: 14 }}>
                Consolidate your assets or supplement your company retirement
                plan under one roof.
              </p>
            </div>
          ),
        },
      ],
    };

    const btnLabel = this.props.hasMixedFunds
      ? "Confirm Both Types"
      : `Confirm, ${
          accountTypesToEnglishMapping[
            this.state
              .selectedAccountType as keyof typeof accountTypesToEnglishMapping
          ]
        } `;

    const cardData = this.props.hasMixedFunds
      ? multiple401kCardData
      : singleAccountCardData;

    return (
      <div id="account-selection">
        <ProgressBar
          isEmployer={false}
          activeStepId={INVESTMENT_PROFILE.id}
          progressPercent={"90"}
        />

        <div className="mega-container">
          <div className="step-container is-active" data-circle-percent="100">
            <section className="page-title-wrap">
              <Row>
                <Col md={7} lg={10}>
                  <article className="text-cell">
                    <p className="page-eyebrow">YOUR INVESTMENT PROFILE</p>
                    <p className="page-title">
                      Let’s confirm the right type of IRA for your Paychex
                      401(k) rollover.
                    </p>
                  </article>
                </Col>
              </Row>
              <article className="progress-cell">
                <ul className="circular-progress-wrap">
                  <CircularProgressBar
                    strokeWidth="8"
                    sqSize="75"
                    percentage="90"
                  />
                </ul>
              </article>
            </section>
            <div>
              <Row style={{ justifyContent: "space-between" }}>
                <Col md={7} lg={7} style={articleStyle}>
                  <div className="paychex-rollover">
                    <div className="paychex-rollover-account-type">
                      <img
                        className="icon-logo"
                        src="/assets/paychex_logo.svg"
                      />

                      <div className="paychex-account-info">
                        <IconHeader
                          variant="labelHeader"
                          headerText={paychexSubtext}
                        />
                      </div>
                    </div>

                    <div className="paychex-divider">
                      <img className="icon-logo" src="/assets/arrow.svg" />
                    </div>

                    <div className="paychex-rollover-account-type">
                      <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <img
                          className="icon-logo"
                          src="/assets/logo_teal.svg"
                        />
                      </div>

                      <div className="paychex-account-info">
                        <IconHeader
                          variant="labelHeader"
                          headerText={iraSubtext}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="submit-row rollover-buttons">
                    {this.props.error && (
                      <Alert type="error" msg={this.props.error} />
                    )}
                  </div>
                  <div
                    className="btn-row rollover-buttons-lg"
                    style={{ paddingBottom: 20 }}
                  >
                    {this.props.hasMixedFunds &&
                      !this.props.isPaychexPerks && (
                        // @ts-expect-error Button `name` is required in props but optional at runtime (omitted here originally)
                        <Button
                          btnLabel="Open One IRA"
                          color="action"
                          onClick={() => {
                            window.analytics.track(
                              "Individual Selected Single IRA Option"
                            );
                            this.setState({ showSelectAccountType: true });
                          }}
                        />
                      )}

                    <Button
                      name="submit"
                      btnLabel={btnLabel}
                      withArrow={true}
                      onClick={this._submitAccountType}
                      loading={this.props.isSubmitting}
                    />
                  </div>
                </Col>
                <Col sm={8} md={5} lg={4} className="rollover-info-card">
                  <IconInfoCard
                    title={cardData.title}
                    // @ts-expect-error legacy card data uses numeric section
                    // keys + ReactNode content (rendered JSX); IconInfoCard's
                    // section type declares string key/content. Runtime behavior
                    // is unchanged — preserved as-is during conversion.
                    sections={cardData.sections}
                    chevron={true}
                  />
                </Col>
              </Row>
              <section className="form-sec-2col">
                <article className="col-form">
                  <div className="submit-row">
                    {this.props.error && (
                      <Alert type="error" msg={this.props.error} />
                    )}
                  </div>
                  <div className="btn-row rollover-buttons-sm">
                    {this.props.hasMixedFunds &&
                      !this.props.isPaychexPerks && (
                        // @ts-expect-error Button `name` is required in props but optional at runtime (omitted here originally)
                        <Button
                          btnLabel="Open One IRA"
                          color="action"
                          onClick={() => {
                            window.analytics.track(
                              "Individual Selected Single IRA Option"
                            );
                            this.setState({ showSelectAccountType: true });
                          }}
                        />
                      )}

                    <Button
                      name="submit"
                      btnLabel={btnLabel}
                      withArrow={true}
                      onClick={this._submitAccountType}
                      loading={this.props.isSubmitting}
                    />
                  </div>
                </article>
              </section>
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    let content;

    if (this.state.showSelectAccountType) {
      content = (
        <Individual401kPartialRollover
          onCancel={() =>
            this.setState({
              showSelectAccountType: false,
            })
          }
        />
      );
    } else {
      content = this._render401kRolloverConfirmation();
    }
    return <div>{content}</div>;
  }
}

export default connector(
  withApollo<Props>(Individual401kConfirmRolloverAccountType)
);
